import { INVISIBLE_COMPONENT_TYPES } from '../../components/InvisibleComponentsBar';
import { sitesApi, pagesApi, componentsApi, layoutsApi, ApiError } from '../../api/client';
import { brandPackagesApi } from '../../api/modules/brandPackages';
import { markEditedPage } from '../../components/GettingStarted';
import { deriveComponentColors, isGlobalPalette } from '../../utils/deriveComponentColors';
import type { PageEditorState } from './usePageEditorState';
import type { ToastType, ToastOptions } from '../../components/Toast';
import type {
  ComponentType,
  CreatePageTemplateRequest,
  Component,
  Site,
} from '../../schemas/validation';

interface HandlerDependencies {
  siteId: string | undefined;
  pageId: string | undefined;
  navigate: (path: string) => void;
  state: PageEditorState;
  showToast: (message: string, type?: ToastType, options?: string | ToastOptions) => void;
}

export const createPageEditorHandlers = ({
  siteId,
  pageId,
  navigate,
  state,
  showToast,
}: HandlerDependencies) => {
  const {
    site,
    setSite,
    setCurrentPage,
    setIsLoading,
    setShowSaveTemplateModal,
    setShowComponentLibraryModal,
    setIsInvisibleComponentMode,
    setShowHeadContentModal,
    setShowBodyEndModal,
    setIsSavingTemplate,
    setIsAddingComponent,
    setShowSuccessToast,
    setSuccessMessage,
    setComponentRefreshTrigger,
    components,
    setComponents,
    editingInvisibleComponent,
    setEditingInvisibleComponent,
    setThemeColors,
    setIsLoadingTheme,
    setThemeError,
    setCurrentThemeName,
    setSitePages,
    insertPosition,
    setInsertPosition,
    setPageLayout,
    duplicateModal,
    setDuplicateModal,
    deleteModal,
    setDeleteModal,
  } = state;

  // Load theme colors only (internal helper)
  const fetchThemeColors = async (themeName: string): Promise<any> => {
    const themeResponse = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/themes/${themeName}?_t=${Date.now()}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      }
    );

    if (!themeResponse.ok) {
      throw new Error(`Failed to load theme: ${themeResponse.status} ${themeResponse.statusText}`);
    }

    return themeResponse.json();
  };

  // Load site colors - checks brand package first, then falls back to theme
  const loadSiteColors = async (siteData: Site, showSuccessNotification: boolean = false) => {
    try {
      setIsLoadingTheme(true);
      setThemeError(null);

      const brandPackageId = (siteData as any).brandPackageId as string | undefined;

      // Priority 1: Brand package
      if (brandPackageId) {
        try {
          console.log('🎨 PageEditor: Loading brand package:', brandPackageId);
          const brandPackage = await brandPackagesApi.get(brandPackageId);
          if (brandPackage) {
            console.log('🎨 PageEditor: Using brand package colors:', brandPackage.name);

            // Re-derive colors from palette to ensure we have latest color derivations
            // This handles brand packages created before new color properties were added
            const palette = (brandPackage.colors as any)?._palette;
            let derivedColors = brandPackage.colors;

            if (palette && isGlobalPalette(palette)) {
              console.log('🎨 PageEditor: Re-deriving colors from palette for latest properties');
              derivedColors = {
                _palette: palette,
                ...deriveComponentColors(palette),
              } as any;
            }

            const colors = {
              ...derivedColors,
              fontFamily: brandPackage.fonts?.bodyFont || 'Inter, system-ui, sans-serif',
              headingFont: brandPackage.fonts?.headingFont || 'Inter, system-ui, sans-serif',
              fontSize: brandPackage.fonts?.baseSize || '16px',
            };
            setThemeColors(colors);
            setCurrentThemeName(`Brand: ${brandPackage.name}`);

            if (showSuccessNotification) {
              showToast(`Brand package "${brandPackage.name}" loaded successfully!`, 'success');
            }
            return;
          }
        } catch (bpError) {
          console.warn('Failed to load brand package, falling back to theme:', bpError);
        }
      }

      // Priority 2: Theme colors
      if (siteData.theme) {
        console.log('🎨 PageEditor: Loading theme colors for:', siteData.theme);
        const themeData = await fetchThemeColors(siteData.theme);
        console.log('🎨 PageEditor: Loaded theme data:', themeData);

        setThemeColors(themeData.customColors);
        setCurrentThemeName(siteData.theme);

        if (showSuccessNotification) {
          showToast(`Theme "${themeData.name || siteData.theme}" loaded successfully!`, 'success');
        }
      }
    } catch (error) {
      console.error('Failed to load site colors:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setThemeError(errorMessage);
      showToast(`Failed to load colors: ${errorMessage}`, 'error');
    } finally {
      setIsLoadingTheme(false);
    }
  };

  // Keep for backwards compatibility with external callers
  const loadThemeColors = async (themeName: string, showSuccessNotification: boolean = false) => {
    if (!themeName) return;

    // If we have a site with brand package, use loadSiteColors instead
    if (site) {
      await loadSiteColors(site, showSuccessNotification);
      return;
    }

    // Fallback: load theme only (no site context)
    try {
      setIsLoadingTheme(true);
      setThemeError(null);

      console.log('🎨 PageEditor: Loading theme colors for:', themeName);
      const themeData = await fetchThemeColors(themeName);
      console.log('🎨 PageEditor: Loaded theme data:', themeData);

      setThemeColors(themeData.customColors);
      setCurrentThemeName(themeName);

      if (showSuccessNotification) {
        showToast(`Theme "${themeData.name || themeName}" loaded successfully!`, 'success');
      }
    } catch (error) {
      console.error('Failed to load theme colors:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setThemeError(errorMessage);
      showToast(`Failed to load theme: ${errorMessage}`, 'error');
    } finally {
      setIsLoadingTheme(false);
    }
  };

  const loadPageData = async () => {
    try {
      setIsLoading(true);

      const allSites = await sitesApi.list();
      const currentSite = allSites.find(s => s.id === siteId);

      if (!currentSite) {
        console.error('Site not found in user sites');
        navigate('/dashboard');
        return;
      }

      setSite(currentSite);

      // Load colors (brand package takes priority over theme)
      await loadSiteColors(currentSite);

      try {
        const pages = await sitesApi.getPages(siteId!);
        const sortedPages = pages.sort((a, b) => {
          const posA = a.orderPosition ?? 999;
          const posB = b.orderPosition ?? 999;
          if (posA !== posB) return posA - posB;
          return a.title.localeCompare(b.title);
        });
        setSitePages(sortedPages);
      } catch (pagesError) {
        console.error('Failed to load site pages:', pagesError);
      }

      try {
        const pageData = await pagesApi.get(pageId!);
        if (pageData.siteId !== siteId) {
          console.error('Page does not belong to this site');
          navigate(`/sites/${siteId}`);
          return;
        }
        setCurrentPage(pageData);
        console.log('🐉 [PageEditor] Loaded page data:', {
          pageId: pageData.id,
          layoutId: pageData.layoutId,
          showLayout: pageData.showLayout,
        });

        const componentsData = await componentsApi.getPageComponents(pageId!);
        setComponents(componentsData.components);

        if (pageData.showLayout !== false) {
          try {
            if (pageData.layoutId) {
              // Specific layout assigned
              const layout = await layoutsApi.get(pageData.layoutId);
              setPageLayout(layout);
            } else {
              // No specific layout — use site default (same rule as live rendering)
              const defaultResult = await layoutsApi.getDefault(siteId!);
              setPageLayout(defaultResult.layout || null);
            }
          } catch (layoutError) {
            console.error('Failed to load page layout:', layoutError);
            setPageLayout(null);
          }
        } else {
          setPageLayout(null);
        }

        // Check for duplicate invisible components
        const invisibleDuplicates = new Map<string, Component[]>();
        componentsData.components.forEach(comp => {
          if (INVISIBLE_COMPONENT_TYPES.includes(comp.type)) {
            if (!invisibleDuplicates.has(comp.type)) {
              invisibleDuplicates.set(comp.type, []);
            }
            invisibleDuplicates.get(comp.type)!.push(comp);
          }
        });

        invisibleDuplicates.forEach((comps, type) => {
          if (comps.length > 1) {
            console.warn(`Found ${comps.length} duplicate ${type} components.`);
            setTimeout(() => {
              setDuplicateModal({ isOpen: true, type, comps });
            }, 1000);
          }
        });
      } catch (pageError) {
        console.error('Failed to load page:', pageError);
        navigate(`/sites/${siteId}`);
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        navigate('/login');
      } else {
        console.error('Failed to load page data:', error);
        navigate('/dashboard');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicateCleanup = async () => {
    const { comps } = duplicateModal;
    const sortedByDate = comps.sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
    const toDelete = sortedByDate.slice(1);

    try {
      await Promise.all(toDelete.map(comp => componentsApi.deleteComponent(pageId!, comp.id)));
      setComponentRefreshTrigger(prev => prev + 1);
      showToast('Duplicate components removed successfully!', 'success');
      setDuplicateModal({ isOpen: false, type: '', comps: [] });
      await refreshPageState();
    } catch (err) {
      console.error('Failed to delete duplicates:', err);
      showToast('Failed to remove duplicates.', 'error');
      setDuplicateModal({ isOpen: false, type: '', comps: [] });
    }
  };

  const handleSaveAsTemplate = async (data: {
    name: string;
    description?: string;
    tags?: string;
    isPublic: boolean;
  }) => {
    try {
      setIsSavingTemplate(true);

      const requestData: CreatePageTemplateRequest = {
        ...data,
        pageId: pageId!,
        siteId: siteId!,
      };

      const response = await pagesApi.saveAsTemplate(pageId!, requestData);

      setSuccessMessage(`Template "${response.template.name}" saved successfully!`);
      setShowSuccessToast(true);
      setShowSaveTemplateModal(false);

      setTimeout(() => {
        setShowSuccessToast(false);
      }, 3000);
    } catch (error: any) {
      console.error('Failed to save template:', error);

      let errorMessage = 'Failed to save template.';
      if (error instanceof Error) {
        errorMessage += ` Error: ${error.message}`;
      }

      showToast(errorMessage, 'error');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const refreshPageState = async () => {
    if (!pageId) return;
    try {
      const freshPage = await pagesApi.get(pageId);
      setCurrentPage(freshPage);
    } catch (err) {
      console.error('Failed to refresh page state:', err);
    }
  };

  const handleAddComponentAt = (position: number) => {
    setInsertPosition(position);
    setShowComponentLibraryModal(true);
  };

  const handleAddComponent = async (componentType: ComponentType) => {
    if (INVISIBLE_COMPONENT_TYPES.includes(componentType)) {
      const existingComponent = components.find(c => c.type === componentType);
      if (existingComponent) {
        const componentName =
          componentType === 'head-content'
            ? 'SEO & Meta Tags'
            : componentType === 'body-end'
              ? 'Scripts & Widgets'
              : componentType;
        showToast(`This page already has a ${componentName} component.`, 'warning');
        setShowComponentLibraryModal(false);
        setInsertPosition(null);

        if (componentType === 'head-content') {
          setEditingInvisibleComponent(existingComponent);
          setShowHeadContentModal(true);
        } else if (componentType === 'body-end') {
          setEditingInvisibleComponent(existingComponent);
          setShowBodyEndModal(true);
        }
        return;
      }
    }

    if (componentType === 'head-content') {
      setShowComponentLibraryModal(false);
      setInsertPosition(null);
      setEditingInvisibleComponent(null);
      setShowHeadContentModal(true);
      return;
    }

    if (componentType === 'body-end') {
      setShowComponentLibraryModal(false);
      setInsertPosition(null);
      setEditingInvisibleComponent(null);
      setShowBodyEndModal(true);
      return;
    }

    try {
      setIsAddingComponent(true);

      const result = await componentsApi.addTemplateComponent(
        pageId!,
        componentType,
        insertPosition ?? undefined
      );
      console.log('Component added successfully:', result);

      markEditedPage();
      setComponentRefreshTrigger(prev => prev + 1);
      setShowComponentLibraryModal(false);
      setInsertPosition(null);

      setSuccessMessage(`${componentType} component added successfully!`);
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);

      await refreshPageState();
    } catch (error) {
      console.error('Failed to add component:', error);

      let errorMessage = `Failed to add ${componentType} component.`;
      if (error instanceof Error) {
        errorMessage += ` Error: ${error.message}`;
      }

      showToast(errorMessage, 'error');
    } finally {
      setIsAddingComponent(false);
      setInsertPosition(null);
    }
  };

  const handlePlaceCompeteForm = async (form: {
    shareToken: string;
    name: string;
    desktopHeight: number;
    mobileHeight: number;
  }) => {
    if (!pageId) return;
    try {
      setIsAddingComponent(true);
      await componentsApi.addTemplateComponent(
        pageId,
        'compete-form',
        {
          shareToken: form.shareToken,
          formName: form.name,
          desktopHeight: form.desktopHeight,
          mobileHeight: form.mobileHeight,
        },
        insertPosition ?? undefined
      );
      markEditedPage();
      setComponentRefreshTrigger(prev => prev + 1);
      setShowComponentLibraryModal(false);
      setInsertPosition(null);
      setSuccessMessage('Compete Studio form added');
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
      await refreshPageState();
    } catch (error) {
      console.error('Failed to add Compete Studio form:', error);
      const message = error instanceof Error ? error.message : 'Failed to add Compete Studio form.';
      showToast(message, 'error');
    } finally {
      setIsAddingComponent(false);
      setInsertPosition(null);
    }
  };

  const handleSaveHeadContent = async (data: any) => {
    try {
      setIsAddingComponent(true);

      if (editingInvisibleComponent) {
        await componentsApi.updateComponent(pageId!, editingInvisibleComponent.id, {
          updates: { data },
        });
        setSuccessMessage('SEO & Meta Tags updated successfully!');
      } else {
        await componentsApi.addTemplateComponent(pageId!, 'head-content', data);
        setSuccessMessage('SEO & Meta Tags configured successfully!');
      }

      setComponentRefreshTrigger(prev => prev + 1);
      setShowHeadContentModal(false);
      setEditingInvisibleComponent(null);

      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);

      await refreshPageState();
    } catch (error) {
      console.error('Failed to save head content component:', error);

      let errorMessage = editingInvisibleComponent
        ? 'Failed to update SEO & Meta Tags.'
        : 'Failed to add SEO & Meta Tags.';
      if (error instanceof Error) {
        errorMessage += ` Error: ${error.message}`;
      }

      showToast(errorMessage, 'error');
    } finally {
      setIsAddingComponent(false);
    }
  };

  const handleSaveBodyEnd = async (data: any) => {
    try {
      setIsAddingComponent(true);

      if (editingInvisibleComponent) {
        await componentsApi.updateComponent(pageId!, editingInvisibleComponent.id, {
          updates: { data },
        });
        setSuccessMessage('Scripts & Widgets updated successfully!');
      } else {
        await componentsApi.addTemplateComponent(pageId!, 'body-end', data);
        setSuccessMessage('Scripts & Widgets configured successfully!');
      }

      setComponentRefreshTrigger(prev => prev + 1);
      setShowBodyEndModal(false);
      setEditingInvisibleComponent(null);

      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);

      await refreshPageState();
    } catch (error) {
      console.error('Failed to save body end component:', error);

      let errorMessage = editingInvisibleComponent
        ? 'Failed to update Scripts & Widgets.'
        : 'Failed to add Scripts & Widgets.';
      if (error instanceof Error) {
        errorMessage += ` Error: ${error.message}`;
      }

      showToast(errorMessage, 'error');
    } finally {
      setIsAddingComponent(false);
    }
  };

  const handleEditInvisibleComponent = (component: Component) => {
    if (component.type === 'head-content') {
      setEditingInvisibleComponent(component);
      setShowHeadContentModal(true);
    } else if (component.type === 'body-end') {
      setEditingInvisibleComponent(component);
      setShowBodyEndModal(true);
    }
  };

  const handleDeleteInvisibleComponent = (componentId: string) => {
    const component = components.find(c => c.id === componentId);
    if (!component) return;
    setDeleteModal({ isOpen: true, component });
  };

  const handleConfirmDelete = async () => {
    const { component } = deleteModal;
    if (!component) return;

    try {
      await componentsApi.deleteComponent(pageId!, component.id);
      setComponentRefreshTrigger(prev => prev + 1);
      showToast('Component deleted successfully!', 'success');
      setDeleteModal({ isOpen: false, component: null });
      await refreshPageState();
    } catch (error) {
      console.error('Failed to delete component:', error);
      showToast('Failed to delete component', 'error');
    }
  };

  const handleAddInvisibleComponent = () => {
    setIsInvisibleComponentMode(true);
    setShowComponentLibraryModal(true);
  };

  const handleAddLibraryComponent = async (libraryComponentId: string) => {
    try {
      setIsAddingComponent(true);

      const result = await componentsApi.addFromLibrary(pageId!, {
        libraryComponentId,
        position: insertPosition ?? undefined,
      });
      console.log('Library component added successfully:', result);

      markEditedPage();
      setComponentRefreshTrigger(prev => prev + 1);
      setShowComponentLibraryModal(false);
      setInsertPosition(null);

      setSuccessMessage('Component added successfully from library!');
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);

      await refreshPageState();
    } catch (error) {
      console.error('Failed to add component from library:', error);

      let errorMessage = 'Failed to add component from library.';
      if (error instanceof Error) {
        errorMessage += ` Error: ${error.message}`;
      }

      showToast(errorMessage, 'error');
    } finally {
      setIsAddingComponent(false);
      setInsertPosition(null);
    }
  };

  const handlePublish = async () => {
    if (!pageId) return;
    state.setIsPublishing(true);
    try {
      const result = await pagesApi.publish(pageId);
      setCurrentPage(result.page);
      showToast('Page published successfully!', 'success', {
        showRoary: true,
        phraseKey: 'generic_success',
      } as ToastOptions);
    } catch (error) {
      console.error('Failed to publish page:', error);
      const message =
        error instanceof ApiError
          ? `Failed to publish: ${error.message}`
          : 'Failed to publish page. Please try again.';
      showToast(message, 'error');
    } finally {
      state.setIsPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!pageId) return;
    if (
      !window.confirm(
        'Are you sure you want to unpublish this page? It will be removed from the live site.'
      )
    ) {
      return;
    }
    state.setIsPublishing(true);
    try {
      const result = await pagesApi.unpublish(pageId);
      setCurrentPage(result.page);
      showToast('Page unpublished. It is no longer visible on the live site.', 'success');
    } catch (error) {
      console.error('Failed to unpublish page:', error);
      const message =
        error instanceof ApiError
          ? `Failed to unpublish: ${error.message}`
          : 'Failed to unpublish page. Please try again.';
      showToast(message, 'error');
    } finally {
      state.setIsPublishing(false);
    }
  };

  return {
    loadThemeColors,
    loadPageData,
    handleDuplicateCleanup,
    handleSaveAsTemplate,
    handleAddComponentAt,
    handleAddComponent,
    handlePlaceCompeteForm,
    handleSaveHeadContent,
    handleSaveBodyEnd,
    handleEditInvisibleComponent,
    handleDeleteInvisibleComponent,
    handleConfirmDelete,
    handleAddInvisibleComponent,
    handleAddLibraryComponent,
    handlePublish,
    handleUnpublish,
  };
};
